import React, { useState } from "react";
import { X, Trash2, Plus, Minus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, total, clear } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant: i.variant })),
        origin_url: window.location.origin,
        email: email || undefined,
      };
      const { data } = await api.post("/checkout/session", payload);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not create checkout session");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div data-testid="cart-drawer" className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[460px] bg-ink border-l border-white/10 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <div className="text-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Your Cart</div>
            <div className="text-display text-3xl tracking-wider text-white mt-1">DROP BAG</div>
          </div>
          <button data-testid="close-cart-btn" onClick={() => setOpen(false)} className="text-white hover:text-adrenaline"><X size={22} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {items.length === 0 && (
            <div className="text-center text-zinc-500 text-mono text-sm py-20">Empty. Hit the drops.</div>
          )}
          {items.map((i) => (
            <div key={i._key} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 border-b border-white/5 pb-5">
              <img src={i.image} alt={i.name} className="w-20 h-24 object-cover bg-carbon" />
              <div className="flex-1">
                <div className="text-heading text-base uppercase text-white">{i.name}</div>
                {i.variant && (
                  <div className="text-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                    {Object.entries(i.variant).map(([k, v]) => `${k}: ${v}`).join(" / ")}
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2 border border-white/10">
                    <button data-testid={`dec-${i.product_id}`} onClick={() => updateQty(i._key, i.quantity - 1)} className="p-1 text-zinc-400 hover:text-white"><Minus size={14} /></button>
                    <span className="text-mono text-sm text-white w-6 text-center">{i.quantity}</span>
                    <button data-testid={`inc-${i.product_id}`} onClick={() => updateQty(i._key, i.quantity + 1)} className="p-1 text-zinc-400 hover:text-white"><Plus size={14} /></button>
                  </div>
                  <div className="text-mono text-white">${(i.price * i.quantity).toFixed(2)}</div>
                </div>
              </div>
              <button data-testid={`remove-${i.product_id}`} onClick={() => removeItem(i._key)} className="text-zinc-500 hover:text-adrenaline self-start"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 p-6 space-y-4">
          <input
            data-testid="cart-email-input"
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email for receipt (optional)"
            className="input-line text-sm"
          />
          <div className="flex items-center justify-between">
            <span className="text-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Subtotal</span>
            <span data-testid="cart-total" className="text-display text-2xl text-white">${total.toFixed(2)}</span>
          </div>
          <button
            data-testid="checkout-btn"
            disabled={loading || items.length === 0}
            onClick={checkout}
            className="w-full py-4 bg-white text-black text-heading uppercase tracking-[0.2em] text-sm hover:bg-adrenaline hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? "Redirecting…" : "Pre-Order · Checkout"}
          </button>
          <p className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 text-center">Pre-order ships 3–5 weeks · 30-day returns</p>
          <button data-testid="clear-cart-btn" onClick={clear} className="w-full py-2 text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-adrenaline">Clear Cart</button>
        </div>
      </aside>
    </div>
  );
}
